class RookieAdjustTensionAnimate extends HTMLElement {
  constructor() {
    super();
    /** @type {RookieAdjustTensionContainer} */
    this.parentContainer = this.closest('rookie-players-adjust-tension-container');
    this.steps = Number(this.getAttribute('data-animate-steps') ?? 0);
    this.currentStep = 1;
    this.isDragging = false;
  }

  connectedCallback() {
    this.initDragging();
    this.initStepClickHandlers();
    this.initBadgeClickHandlers(); // 新增：初始化badge点击处理器
    this.updateProgress();
    this.updateImageDisplacement();
  }

  disconnectedCallback() {
    this.removeDraggingListeners();
  }

  initDragging() {
    // 找到所有进度条和手柄
    const progressBars = this.querySelectorAll('.rookie-players-adjust-tension__overlay__bottom-bar__progress');

    progressBars.forEach(progressBar => {
      const handle = progressBar.querySelector('span');
      if (!handle) return;

      // 添加鼠标事件
      handle.addEventListener('mousedown', this.startDrag.bind(this));
      handle.addEventListener('touchstart', this.startDrag.bind(this));

      // 添加点击进度条切换功能
      progressBar.addEventListener('click', this.handleProgressClick.bind(this));
      // 添加移动到进度条上暂停循环的功能
      progressBar.addEventListener('mouseenter', this.handleProgressEnter.bind(this));
      progressBar.addEventListener('mouseleave', this.handleProgressLeave.bind(this));
    });

    // 添加全局事件
    document.addEventListener('mousemove', this.drag.bind(this));
    document.addEventListener('mouseup', this.endDrag.bind(this));
    document.addEventListener('touchmove', this.drag.bind(this));
    document.addEventListener('touchend', this.endDrag.bind(this));
  }

  initStepClickHandlers() {
    // 找到所有步骤标签
    const stepElements = this.querySelectorAll('.rookie-players-adjust-tension__overlay__bottom-bar__step');

    stepElements.forEach((stepElement, index) => {
      const stepSpan = stepElement.querySelector('span');
      if (!stepSpan) return;

      // 添加点击事件
      stepSpan.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleStepClick(stepElement, index + 1); // index从0开始，步数从1开始
      });

      // 添加鼠标样式
      stepSpan.style.cursor = 'pointer';
    });
  }

  initBadgeClickHandlers() {
    // 获取所有左badge和右badge
    const leftBadges = this.querySelectorAll('.rookie-players-adjust-tension__overlay__bottom-bar__left-badge');
    const rightBadges = this.querySelectorAll('.rookie-players-adjust-tension__overlay__bottom-bar__right-badge');

    // 为左badge添加点击事件（减一档）
    leftBadges.forEach(badge => {
      badge.addEventListener('click', (e) => {
        this.parentContainer.reset();
        e.preventDefault();
        e.stopPropagation();
        this.decreaseStep();
      });
    });

    // 为右badge添加点击事件（加一档）
    rightBadges.forEach(badge => {
      badge.addEventListener('click', (e) => {
        this.parentContainer.reset();
        e.preventDefault();
        e.stopPropagation();
        this.increaseStep();
      });
    });
  }

  decreaseStep() {
    // 减一档，但确保不小于1
    const newStep = Math.max(1, this.currentStep - 1);
    if (newStep !== this.currentStep) {
      this.setStep(newStep);
      this.highlightActiveStep(newStep);
    }
  }

  increaseStep() {
    // 加一档，但确保不超过总步数
    const newStep = Math.min(this.steps, this.currentStep + 1);
    if (newStep !== this.currentStep) {
      this.setStep(newStep);
      this.highlightActiveStep(newStep);
    }
  }

  loopIncreaseStep() {
    // 加一档，超过总步数时回到1重新循环
    let newStep = this.currentStep + 1;

    if (newStep > this.steps) {
      newStep = 1; // 循环回到第一步
    }

    this.setStep(newStep);
    this.highlightActiveStep(newStep);
  }

  handleStepClick(stepElement, stepNumber) {
    // 确保步数在有效范围内
    stepNumber = Math.max(1, Math.min(this.steps, stepNumber));

    // 设置步骤
    this.setStep(stepNumber);

    // 可以添加一些视觉反馈，比如高亮当前步骤
    this.highlightActiveStep(stepNumber);
  }

  highlightActiveStep(stepNumber) {
    // 移除所有步骤的高亮
    const stepElements = this.querySelectorAll('.rookie-players-adjust-tension__overlay__bottom-bar__step');
    stepElements.forEach(stepElement => {
      stepElement.classList.remove('active-step');
    });

    // 添加当前步骤的高亮
    const activeStep = this.querySelector(`.rookie-players-adjust-tension__overlay__bottom-bar__step:nth-child(${stepNumber})`);
    if (activeStep) {
      activeStep.classList.add('active-step');
    }
  }

  startDrag(e) {
    e.preventDefault();
    e.stopPropagation();
    this.isDragging = true;

    const handle = e.target.closest('.rookie-players-adjust-tension__overlay__bottom-bar__progress span');
    if (!handle) return;

    this.currentHandle = handle;
    this.currentProgressBar = handle.closest('.rookie-players-adjust-tension__overlay__bottom-bar__progress');

    // 添加dragging类
    this.currentProgressBar.classList.add('dragging');

    // 禁用过渡效果，使拖拽更流畅
    this.currentProgressBar.style.transition = 'none';
    this.currentHandle.style.transition = 'none';

    // 创建一个临时的样式来覆盖伪元素的过渡
    const styleId = 'temp-transition-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
          .rookie-players-adjust-tension__overlay__bottom-bar__progress.dragging::before {
            transition: none !important;
          }
        `;
      document.head.appendChild(style);
    }

    handle.style.cursor = 'grabbing';
  }

  drag(e) {
    if (!this.isDragging || !this.currentProgressBar) return;

    e.preventDefault();
    e.stopPropagation();

    const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
    const rect = this.currentProgressBar.getBoundingClientRect();
    let relativeX = clientX - rect.left;

    // 限制在进度条范围内
    relativeX = Math.max(0, Math.min(rect.width, relativeX));

    // 计算当前进度百分比
    const progress = relativeX / rect.width;

    // 直接设置CSS变量，不使用过渡
    this.currentProgressBar.style.setProperty('--progress', progress);

    // 计算最接近的档位
    const closestStep = this.getClosestStep(progress);

    // 保存当前最接近的档位，在拖拽结束时使用
    this.draggingStep = closestStep;

    // 拖拽时实时更新当前步骤
    if (closestStep !== this.currentStep) {
      this.setStep(closestStep, true); // true表示是拖拽中实时更新
    }
  }

  getClosestStep(progress) {
    const stepCount = this.steps;
    if (stepCount <= 1) return 1;

    // 计算每个档位的位置（0%, 50%, 100%）
    const stepPositions = [];
    for (let i = 1; i <= stepCount; i++) {
      const position = (i - 1) / (stepCount - 1);
      stepPositions.push({ step: i, position });
    }

    // 找到最接近的档位
    let closestStep = 1;
    let minDistance = 1;

    stepPositions.forEach(({ step, position }) => {
      const distance = Math.abs(progress - position);
      if (distance < minDistance) {
        minDistance = distance;
        closestStep = step;
      }
    });

    return closestStep;
  }

  endDrag(e) {
    if (!this.isDragging) return;

    e.preventDefault();
    e.stopPropagation();
    this.isDragging = false;

    // 移除dragging类
    if (this.currentProgressBar) {
      this.currentProgressBar.classList.remove('dragging');
    }

    // 移除临时样式
    const styleId = 'temp-transition-style';
    const tempStyle = document.getElementById(styleId);
    if (tempStyle) {
      tempStyle.remove();
    }

    // 恢复过渡效果
    if (this.currentHandle) {
      this.currentHandle.style.cursor = 'grab';
      this.currentHandle.style.transition = '';
    }

    if (this.currentProgressBar) {
      this.currentProgressBar.style.transition = '';
    }

    // 如果有拖拽中的档位，跳转到该档位
    if (this.draggingStep) {
      // 先直接设置到目标位置
      const stepCount = this.steps;
      const progress = (this.draggingStep - 1) / (stepCount - 1);
      this.currentProgressBar.style.setProperty('--progress', progress);

      // 启用过渡
      setTimeout(() => {
        if (this.currentProgressBar) {
          this.currentProgressBar.style.transition = '';
        }
      }, 10);

      // 更新步骤并高亮
      this.setStep(this.draggingStep);
      this.highlightActiveStep(this.draggingStep);
      this.draggingStep = null;
    }

    this.currentHandle = null;
    this.currentProgressBar = null;
  }

  handleProgressClick(e) {
    // 如果是从手柄开始的点击，让拖拽逻辑处理
    if (e.target.closest('.rookie-players-adjust-tension__overlay__bottom-bar__progress span')) {
      return;
    }

    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const progress = Math.max(0, Math.min(1, clickX / rect.width));

    // 计算并跳转到最接近的档位
    const closestStep = this.getClosestStep(progress);
    this.setStep(closestStep);
    this.highlightActiveStep(closestStep);
  }

  handleProgressEnter(e) {
    this.parentContainer.stop();
  }

  handleProgressLeave(e) {
    this.parentContainer.reset();
  }

  setStep(step, isDragging = false) {
    // 确保步数在有效范围内
    step = Math.max(1, Math.min(this.steps, step));

    // 更新当前步骤
    this.currentStep = step;
    this.setAttribute('data-current-step', step);

    // 更新所有进度条的位置
    this.updateProgress();

    // 更新图片位移
    this.updateImageDisplacement();
  }

  updateProgress() {
    const progressBars = this.querySelectorAll('.rookie-players-adjust-tension__overlay__bottom-bar__progress');
    const stepCount = this.steps;

    if (stepCount <= 1) return;

    // 计算当前步骤对应的进度（0%, 50%, 100%）
    const progress = (this.currentStep - 1) / (stepCount - 1);

    progressBars.forEach(progressBar => {
      // 只有在不是正在拖拽的情况下才使用过渡
      if (!this.isDragging || progressBar !== this.currentProgressBar) {
        progressBar.style.setProperty('--progress', progress);
      }
    });
  }

  updateImageDisplacement() {
    // 根据当前步骤设置图片的位移
    const stepCount = this.steps;
    if (stepCount <= 1) return;

    // 计算位移比例 (0%, 50%, 100%)
    const displacementRatio = (this.currentStep - 1) / (stepCount - 1);

    // 更新CSS变量
    const rightContainers = this.querySelectorAll('.rookie-players-adjust-tension__overlay__animate-right');
    rightContainers.forEach(container => {
      container.style.setProperty('--displacement-ratio', displacementRatio);
    });
  }

  removeDraggingListeners() {
    // 清理事件监听器
    const progressBars = this.querySelectorAll('.rookie-players-adjust-tension__overlay__bottom-bar__progress');

    progressBars.forEach(progressBar => {
      const handle = progressBar.querySelector('span');
      if (handle) {
        handle.removeEventListener('mousedown', this.startDrag.bind(this));
        handle.removeEventListener('touchstart', this.startDrag.bind(this));
      }
      progressBar.removeEventListener('click', this.handleProgressClick.bind(this));
    });

    document.removeEventListener('mousemove', this.drag.bind(this));
    document.removeEventListener('mouseup', this.endDrag.bind(this));
    document.removeEventListener('touchmove', this.drag.bind(this));
    document.removeEventListener('touchend', this.endDrag.bind(this));
  }
}

if (!customElements.get('rookie-players-adjust-tension-animate')) {
  customElements.define('rookie-players-adjust-tension-animate', RookieAdjustTensionAnimate);
}

class RookieAdjustTensionContainer extends HTMLElement {
  constructor() {
    super();
    this.timeLoop = 2000;
    /** @type {NodeListOf<RookieAdjustTensionAnimate>} */
    this.animateList = this.querySelectorAll('rookie-players-adjust-tension-animate');
  }

  connectedCallback() {
    this.start();
  }

  start() {
    this.interval = setInterval(() => {
      for (const element of this.animateList) {
        element.loopIncreaseStep();
      }
    }, this.timeLoop);
  }

  stop() {
    clearInterval(this.interval);
  }

  reset() {
    clearInterval(this.interval);
    this.start();
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }
}

if (!customElements.get('rookie-players-adjust-tension-container')) {
  customElements.define('rookie-players-adjust-tension-container', RookieAdjustTensionContainer);
}